import { useCallback, useEffect, useState } from "react";
import {
  deleteLocalBook,
  getLocalBook,
  listLocalBooks,
  saveLocalBook,
  updateLocalBookProgress,
} from "../lib/localLibrary";
import type { StoredPdf } from "../types";

export function usePdfLibrary() {
  const [documents, setDocuments] = useState<StoredPdf[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setDocuments(await listLocalBooks());
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Kitap arşivi yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const upload = useCallback(
    async (file: File) => {
      setUploadProgress(10);
      setError("");
      try {
        const id = await saveLocalBook(file);
        setUploadProgress(100);
        await refresh();
        return id;
      } finally {
        setUploadProgress(null);
      }
    },
    [refresh],
  );

  const openDocument = useCallback(
    (document: StoredPdf): Promise<File> => getLocalBook(document),
    [],
  );

  const removeDocument = useCallback(
    async (document: StoredPdf) => {
      await deleteLocalBook(document.id);
      await refresh();
    },
    [refresh],
  );

  const updateProgress = useCallback(async (id: string, page: number) => {
    await updateLocalBookProgress(id, page);
    setDocuments((current) =>
      current.map((document) =>
        document.id === id ? { ...document, lastPage: page } : document,
      ),
    );
  }, []);

  return {
    documents,
    loading,
    uploadProgress,
    error,
    refresh,
    upload,
    openDocument,
    removeDocument,
    updateProgress,
  };
}
